import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { extraArtistAddOnMonthlyCents } from 'fym-shared/src/music24/monthlyAddOns';
import { MUSIC24_MONTHS_PER_YEARLY_PRICE } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../../common/types';
import { planNameMessages } from '../../../music24/config/music24PlanNames';
import { useLicenseInfo } from '../../../../redux/slices/license/hooks/hooks';
import { AddOnSliderRow } from './AddOnSliderRow';
import { AddOnTotal } from './AddOnTotal';
import { AddOnAlert } from './AddOnAlert';
import { MoreArtistsLine } from './MoreArtistsLine';
import {
  type AddOnDraft,
  type AddOnRowKind,
  addOnChange,
  addOnRowModel,
  addOnsTotal,
  extraArtistRowModel,
  perArtistShare,
} from './addOnRowModel';
import { addOnFacts } from './addOnFacts';
import { formatAddOnCents } from './addOnFormat';
import { useAddOnPreset } from './useAddOnPreset';
import { monthlyAddOnMessages as messages } from './monthlyAddOnMessages';
import type { MonthlyAddOnsData, MonthlyAddOnsSave } from './monthlyAddOnTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the add-ons panel; each part uses its own slice of it */
import styles from './MonthlyAddOns.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MonthlyAddOnsEditorProps {
  data: MonthlyAddOnsData;
  save: MonthlyAddOnsSave;
}

/**
 * The owner of a web plan: the sliders, the live total and Save (design monthly-addons a1-a6, b6-b9).
 * Plus, Pro, Max and Ultra get a third row for the extra artists, each at the plan price minus
 * 20 %. Plug gets one line that names the plans that add artists. The price and the limits change while a slider moves,
 * with the fym-shared tables, so the screen and the Stripe invoice agree. One Save sends every
 * value to `billing.setMonthlyAddOns`.
 */
const MonthlyAddOnsEditorComponent: React.FC<MonthlyAddOnsEditorProps> = ({ data, save }) => {
  const intl = useIntl();
  const renewsAt = useLicenseInfo().subscriptionExpiresAt;
  const artists = data.extraArtists;
  // The saved values. The extra artists are 0 on a plan with no artists row.
  const savedPosts = data.current.posts;
  const savedCredits = data.current.videoCredits;
  const savedArtists = artists.offered ? artists.current : 0;
  const saved = useMemo<AddOnDraft>(
    () => ({ posts: savedPosts, videoCredits: savedCredits, extraArtists: savedArtists }),
    [savedPosts, savedCredits, savedArtists]
  );
  const [draft, setDraft] = useState<AddOnDraft>(saved);
  const [savedNow, setSavedNow] = useState(false);
  const [failed, setFailed] = useState(false);

  // A new read (after a save, or a change on another device) is the new start.
  useEffect(() => {
    setDraft(saved);
  }, [saved]);
  // design: limit-upsell a1 "Choose another amount" (?addOnPosts= / ?addOnCredits=).
  useAddOnPreset(saved, setDraft);

  const interval = data.interval ?? 'monthly';
  const planName = intl.formatMessage(planNameMessages[data.plan]);
  const change = addOnChange(draft, saved);
  const saving = save.isPending;
  // The credits of a workspace with extra artists are one pool for all its artists.
  const isTeam = artists.offered && draft.extraArtists > 0;
  // Each extra artist adds the posts and the credits of one artist (decision D2), so the posts row
  // and the credits row move with the artists slider. The share of one artist comes from the server
  // limits of the included artists, not from the plan table: during the trial the server gives the
  // trial limits, and a save then multiplies those.
  const postsPerArtist = perArtistShare(data.baseLimits.scheduledPostsPerMonth, artists.includedArtists);
  const creditsPerArtist = perArtistShare(data.baseLimits.videoCreditsPerMonth, artists.includedArtists);
  const artistPosts = artists.offered ? draft.extraArtists * postsPerArtist : 0;
  const artistCredits = artists.offered ? draft.extraArtists * creditsPerArtist : 0;

  const rows = useMemo(
    () => ({
      posts: addOnRowModel({
        kind: 'posts',
        quantity: draft.posts,
        baseLimit:
          data.baseLimits.scheduledPostsPerMonth === null ? null : data.baseLimits.scheduledPostsPerMonth + artistPosts,
        interval,
      }),
      videoCredits: addOnRowModel({
        kind: 'videoCredits',
        quantity: draft.videoCredits,
        baseLimit:
          data.baseLimits.videoCreditsPerMonth === null ? null : data.baseLimits.videoCreditsPerMonth + artistCredits,
        interval,
      }),
      extraArtists: extraArtistRowModel({
        plan: data.plan,
        quantity: draft.extraArtists,
        min: artists.minimum,
        max: artists.max,
        step: artists.step,
        baseLimit: data.baseLimits.artistsLimit,
        interval,
      }),
    }),
    [draft, data.baseLimits, data.plan, interval, artists, artistPosts, artistCredits]
  );
  const total = useMemo(
    () =>
      addOnsTotal({
        planPeriodCents: data.planPeriodCents,
        addOns: draft,
        interval,
        ...(artists.offered ? { extraArtists: { plan: data.plan, quantity: draft.extraArtists } } : {}),
      }),
    [data.planPeriodCents, data.plan, draft, interval, artists.offered]
  );

  const setKind = useCallback((kind: AddOnRowKind, quantity: number) => {
    setSavedNow(false);
    setFailed(false);
    setDraft((previous) => ({ ...previous, [kind]: quantity }));
  }, []);
  const setPosts = useCallback((quantity: number) => setKind('posts', quantity), [setKind]);
  const setCredits = useCallback((quantity: number) => setKind('videoCredits', quantity), [setKind]);
  const setArtists = useCallback((quantity: number) => setKind('extraArtists', quantity), [setKind]);
  const reset = useCallback(() => {
    setFailed(false);
    setDraft(saved);
  }, [saved]);
  const submit = useCallback(() => {
    setFailed(false);
    const input = artists.offered ? draft : { posts: draft.posts, videoCredits: draft.videoCredits };
    save.mutate(input, {
      onSuccess: () => setSavedNow(true),
      onError: (error) => {
        console.error(error);
        setFailed(true);
      },
    });
  }, [draft, save, artists.offered]);

  const priceText = (cents: number) =>
    intl.formatMessage(interval === 'yearly' ? messages.priceYear : messages.priceMonth, {
      price: formatAddOnCents(intl, cents),
    });
  const nowLimits: Record<AddOnRowKind, number | null> = {
    posts: data.limits.scheduledPostsPerMonth,
    videoCredits: data.limits.videoCreditsPerMonth,
    extraArtists: data.limits.artistsLimit,
  };
  const facts = (kind: AddOnRowKind) =>
    addOnFacts(intl, {
      kind,
      model: rows[kind],
      plan: planName,
      teamPool: kind === 'videoCredits' && isTeam,
      lowered: draft[kind] < saved[kind],
      nowLimit: nowLimits[kind],
      nextPeriod: renewsAt ?? null,
    });
  const artistsNote =
    artists.offered && artists.minimum > 0
      ? intl.formatMessage(messages.artistsInUse, { artists: artists.artistCount, inUse: artists.inUse })
      : null;
  // Ultra has no cap. Plus, Pro and Max stop at their cap; more artists need Ultra.
  const artistsHint =
    artists.offered && data.plan !== 'label'
      ? intl.formatMessage(messages.artistsManagerCap, { max: artists.max, plan: planName })
      : null;

  return (
    <>
      {savedNow && !change.changed && (
        <div className={styles.body}>
          <AddOnAlert tone="ok">
            {artists.offered
              ? intl.formatMessage(messages.savedTeam, {
                  posts: intl.formatNumber(data.limits.scheduledPostsPerMonth ?? 0),
                  credits: intl.formatNumber(data.limits.videoCreditsPerMonth ?? 0),
                  artists: intl.formatNumber(data.limits.artistsLimit ?? 0),
                })
              : intl.formatMessage(messages.saved, {
                  posts: intl.formatNumber(data.limits.scheduledPostsPerMonth ?? 0),
                  credits: intl.formatNumber(data.limits.videoCreditsPerMonth ?? 0),
                })}
          </AddOnAlert>
        </div>
      )}
      <AddOnSliderRow
        model={rows.posts}
        name={intl.formatMessage(messages.postsName)}
        sub={intl.formatMessage(messages.postsSub)}
        facts={facts('posts')}
        price={priceText(rows.posts.periodCents)}
        disabled={saving}
        onChange={setPosts}
      />
      <AddOnSliderRow
        model={rows.videoCredits}
        name={intl.formatMessage(messages.creditsName)}
        sub={intl.formatMessage(isTeam ? messages.creditsPoolSub : messages.creditsSub)}
        facts={facts('videoCredits')}
        price={priceText(rows.videoCredits.periodCents)}
        disabled={saving}
        onChange={setCredits}
      />
      {artists.offered ? (
        <AddOnSliderRow
          model={rows.extraArtists}
          name={intl.formatMessage(messages.artistsName)}
          sub={intl.formatMessage(interval === 'yearly' ? messages.artistsSubYear : messages.artistsSub, {
            // A yearly plan bills one extra artist for ten months a year.
            price: formatAddOnCents(
              intl,
              extraArtistAddOnMonthlyCents(data.plan, 1) * (interval === 'yearly' ? MUSIC24_MONTHS_PER_YEARLY_PRICE : 1)
            ),
            plan: planName,
            posts: intl.formatNumber(postsPerArtist),
            credits: intl.formatNumber(creditsPerArtist),
          })}
          facts={facts('extraArtists')}
          price={priceText(rows.extraArtists.periodCents)}
          disabled={saving}
          onChange={setArtists}
          unit={intl.formatMessage(messages.artistsUnit)}
          valueText={intl.formatMessage(messages.artistsValueText, { count: draft.extraArtists })}
          note={artistsNote}
          hint={artistsHint}
        />
      ) : (
        <MoreArtistsLine />
      )}
      {(change.drops || failed) && (
        <div className={styles.body}>
          {change.drops && (
            <AddOnAlert tone="info">
              {renewsAt === undefined
                ? intl.formatMessage(messages.lowerNoteNoDate)
                : intl.formatMessage(messages.lowerNote, {
                    date: intl.formatDate(renewsAt, { year: 'numeric', month: 'short', day: 'numeric' }),
                  })}
            </AddOnAlert>
          )}
          {failed && <AddOnAlert tone="error">{intl.formatMessage(messages.saveFailed)}</AddOnAlert>}
        </div>
      )}
      <AddOnTotal
        planName={planName}
        planPeriodCents={data.planPeriodCents}
        addOnsCents={total.addOnsCents}
        totalCents={total.totalCents}
        monthlyCents={total.monthlyCents}
        interval={interval}
        changed={change.changed}
        saving={saving}
        onSave={submit}
        onReset={reset}
      />
      {change.rises && <div className={styles.foot}>{intl.formatMessage(messages.riseFoot)}</div>}
    </>
  );
};

export const MonthlyAddOnsEditor = TypedMemo(MonthlyAddOnsEditorComponent);
